/**
 * ComponentRunner — 학습 세션에서 단일 컴포넌트를 실행하는 공통 쉘
 *
 * - 진입 시 startSession 호출 → Runner 선택 → Runner 완료 시 onComplete 로 즉시 다음 단계 이동
 * - DESIGN.md §5 Top App Bar (72px) + §7 Progress strip
 *
 * 옵션(skipPolicy · speedPolicy · pauseEnabled · showHint · maxAttempts · ...) 은
 * 이번 스펙 범위 외. 해당 UI 는 각 Runner 에 항상 노출되며 // TODO(option) 주석
 * 으로 표시된다. Spec: §7.
 */
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  startSession,
  completeSession,
  type ComponentCompleteResult,
  type LcComponentStatus,
  type LcStartSessionResponse,
  type MaterialNotice,
} from '@/api/lcLearning'
import {
  startLibraryReview,
  type LcLibraryReviewMode,
  type LcLibraryStartResponse,
} from '@/api/lcLibraryReview'
import { pickComponentLabel, type ComponentLabelFallbacks } from '@/utils/componentLabel'
import { Button3D } from './ui/Button3D'
import { Card } from './ui/Card'
import { LearnerLoading } from './ui/LearnerLoading'
import { WrongReviewIntroDialog } from './WrongReviewIntroDialog'
import VideoPlayer from './VideoPlayer'
import QuizRunner from './QuizRunner'
import GuideView from './GuideView'
import BuildupView from './BuildupView'
import WarmupView from './WarmupView'
import ToonRunner from './ToonRunner'
import ToolRunner from './ToolRunner'
import {
  applyLibraryVideoReviewMode,
  type LibraryVideoReviewMode,
} from './libraryVideoReview'

/**
 * 런타임 모드.
 * - 'live' (default): 오늘의 수업 흐름. session/progress 기록 O.
 * - 'library-review': 도서관 복습. 모든 진행 DB write 생략. 퀴즈는 gradeLibraryQuiz 로 채점만.
 */
export type RuntimeMode = 'live' | 'library-review'

interface Props {
  enrollmentId: number
  lessonId: number
  component: LcComponentStatus
  isFirstAttempt: boolean
  /** 현재 컴포넌트의 0-based 인덱스 (TodayClass 에서 sorted 이후 전달) */
  currentIndex: number
  /** 전체 컴포넌트 수 */
  totalCount: number
  /** 완료/건너뛰기 결과. 생략(기존 Runner)은 completed. */
  onComplete: (result?: ComponentCompleteResult) => void
  onBack: () => void
  /** true 면 내부 sticky header/progress strip 을 렌더하지 않음. 부모 레이아웃이 상단바를 제공할 때 사용. */
  headless?: boolean
  /** 기본 'live' — 오늘의 수업 흐름. 'library-review'는 격리 세션(quiz)·read-only(비-quiz). */
  runtimeMode?: RuntimeMode
  /** library-review 퀴즈 모드 — full(전체) | wrong_only(오답만). 기본 full. */
  reviewMode?: LcLibraryReviewMode
  /** library-review 영상 모드. 기본은 기존 동작인 영상 + 블록퀴즈. */
  videoReviewMode?: LibraryVideoReviewMode
  /** 교사가 지정한 복습 레슨 여부 — 하위 플레이어(VideoPlayer/BuildupView)에 전달. */
  isDesignatedReview?: boolean
  /** 깨처 교재 펼침 팝업에 표시할 단일 교재. */
  materialNotice?: MaterialNotice | null
  /** 도서관 퀴즈를 모두 채점한 뒤 상단 닫기를 눌렀는지 여부. */
  exitConfirmationRequested?: boolean
  onQuizCompletionReadyChange?: (ready: boolean) => void
  onExitWithoutComplete?: () => void
}

export default function ComponentRunner({
  enrollmentId,
  lessonId,
  component,
  isFirstAttempt,
  currentIndex,
  totalCount,
  onComplete,
  onBack,
  headless = false,
  runtimeMode = 'live',
  reviewMode = 'full',
  videoReviewMode = 'video_with_block_quiz',
  isDesignatedReview = false,
  materialNotice = null,
  exitConfirmationRequested = false,
  onQuizCompletionReadyChange,
  onExitWithoutComplete,
}: Props) {
  const { t, i18n } = useTranslation('learner')
  // 컴포넌트 semantic 라벨 fallback(명시 label 없을 때만). 예: 반짝깨처(직전 레슨 flash 영상).
  const labelFallbacks = useMemo<ComponentLabelFallbacks>(
    () => ({ flashQueture: t('learn.componentName.flashQueture') }),
    [t],
  )
  const [session, setSession] = useState<LcStartSessionResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [skipping, setSkipping] = useState(false)
  const [wrongReviewIntroAcknowledged, setWrongReviewIntroAcknowledged] = useState(false)
  const isVideoComponent = component.type.toLowerCase().includes('video')
  const isToonComponent = component.type.toLowerCase() === 'toon'
  const isQuizComponent = component.type.toLowerCase().includes('quiz')
  const isWarmupComponent = component.type.toLowerCase() === 'warmup'
  const isBuildupComponent = component.type.toLowerCase() === 'buildup'
  const usesV3VideoLayout =
    isVideoComponent && session?.videoData != null

  // G5: 인세션 오답 풀기(wrong_in_review) 컴포넌트 여부 — config-v2 extraction.answerFilter 로 식별.
  const isWrongReview =
    component.type.toLowerCase().includes('quiz') &&
    (component.options as { extraction?: { answerFilter?: string } } | null)
      ?.extraction?.answerFilter === 'wrong_in_review'

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setWrongReviewIntroAcknowledged(false)

    const fetcher: () => Promise<LcStartSessionResponse> =
      runtimeMode === 'library-review'
        ? () =>
            startLibraryReview({
              enrollmentId,
              lessonId,
              configComponentId: component.configComponentId,
              componentType: component.type,
              mode: reviewMode,
            }).then(adaptLibraryStartResponse)
        : () =>
            startSession({
              enrollmentId,
              lessonId,
              configComponentId: component.configComponentId,
              componentType: component.type,
            })

    fetcher()
      .then((data) => {
        if (!cancelled) setSession(data)
      })
      .catch((e) => {
        console.error('startSession failed', e)
        if (!cancelled) setError(t('learn.runner.sessionError'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [enrollmentId, lessonId, component.configComponentId, component.type, t, runtimeMode, reviewMode])

  // G5 엣지 2: 오답 풀기 컴포넌트인데 이번 복습 회차 오답이 0개면(quizData 없음) 출제 없이
  // 빈 격리 세션을 완료 처리하고 다음 단계로 자동 통과(게이트 스킵). live(오늘 수업)에서만.
  useEffect(() => {
    if (loading || error || skipping) return
    if (runtimeMode !== 'live' || !isWrongReview) return
    if (!session || session.quizData) return
    if (session.componentProgressId == null) {
      onComplete()
      return
    }
    setSkipping(true)
    completeSession(session.componentProgressId, {
      enrollmentId,
      componentType: component.type,
      passScore: 0,
      maxAttempts: 0,
    })
      .catch((e) => console.error('wrong_review auto-skip complete failed', e))
      .finally(() => onComplete())
    // onComplete 은 부모에서 안정적이지 않을 수 있어 의존성에서 제외(스킵은 1회성, skipping 가드).
  }, [session, loading, error, skipping, isWrongReview, runtimeMode, enrollmentId, component.type])

  function handleChildComplete(result?: ComponentCompleteResult) {
    // 실제 completeSession/skipSession 호출은 각 child runner 내부에서 이루어지며,
    // child runner들이 runtimeMode === 'live' 조건으로 가드한다. 결과(completed|skipped)만 전파한다.
    onComplete(result)
  }

  function handleRetry() {
    setError(null)
    setLoading(true)

    const fetcher: () => Promise<LcStartSessionResponse> =
      runtimeMode === 'library-review'
        ? () =>
            startLibraryReview({
              enrollmentId,
              lessonId,
              configComponentId: component.configComponentId,
              componentType: component.type,
              mode: reviewMode,
            }).then(adaptLibraryStartResponse)
        : () =>
            startSession({
              enrollmentId,
              lessonId,
              configComponentId: component.configComponentId,
              componentType: component.type,
            })

    fetcher()
      .then(setSession)
      .catch((e) => {
        console.error('startSession retry failed', e)
        setError(t('learn.runner.sessionError'))
      })
      .finally(() => setLoading(false))
  }

  function renderRunner() {
    if (!session) return null
    const type = component.type.toLowerCase()

    if (type === 'toon') {
      const toonType =
        ((component.options as { toonType?: string } | null)?.toonType as
          | 'intro'
          | 'outro') ?? 'intro'
      return (
        <ToonRunner
          componentProgressId={session.componentProgressId}
          enrollmentId={enrollmentId}
          componentType={component.type}
          toonType={toonType}
          toonData={session.toonData ?? null}
          runtimeMode={runtimeMode}
          onComplete={handleChildComplete}
        />
      )
    }

    if (type.includes('video')) {
      if (!session.videoData) {
        return (
          <Card size="md" className="text-center text-lc-gray-600">
            {t('learn.runner.noVideo')}
          </Card>
        )
      }
      const videoData =
        runtimeMode === 'library-review'
          ? applyLibraryVideoReviewMode(session.videoData, videoReviewMode)
          : session.videoData
      return (
        <VideoPlayer
          componentProgressId={session.componentProgressId}
          enrollmentId={enrollmentId}
          componentType={component.type}
          videoData={videoData}
          isFirstAttempt={isFirstAttempt}
          materialNotice={materialNotice}
          isDesignatedReview={isDesignatedReview}
          runtimeMode={runtimeMode}
          onComplete={handleChildComplete}
        />
      )
    }

    if (type.includes('quiz')) {
      if (!session.quizData) {
        // G5: 오답 풀기인데 오답 0개 → 자동 통과 중(빈 게이트). dead-end 카드 대신 통과 안내.
        if (isWrongReview) {
          return (
            <Card size="md" className="text-center text-lc-gray-600">
              {t('learn.runner.wrongReviewSkip')}
            </Card>
          )
        }
        return (
          <Card size="md" className="text-center text-lc-gray-600">
            {t('learn.runner.noQuiz')}
          </Card>
        )
      }
      return (
        <QuizRunner
          componentProgressId={session.componentProgressId}
          sessionId={session.sessionId ?? session.componentProgressId}
          enrollmentId={enrollmentId}
          componentType={component.type}
          quizData={session.quizData}
          runtimeMode={runtimeMode}
          onRetryRequired={handleRetry}
          onComplete={handleChildComplete}
          exitConfirmationRequested={exitConfirmationRequested}
          onCompletionReadyChange={onQuizCompletionReadyChange}
          onExitWithoutComplete={onExitWithoutComplete}
        />
      )
    }

    if (type === 'interactive') {
      if (!session.toolData) {
        return (
          <Card size="md" className="text-center text-lc-gray-600">
            {t('learn.runner.noTool')}
          </Card>
        )
      }
      return (
        <ToolRunner
          componentProgressId={session.componentProgressId}
          enrollmentId={enrollmentId}
          componentType={component.type}
          toolData={session.toolData}
          runtimeMode={runtimeMode}
          onComplete={handleChildComplete}
        />
      )
    }

    if (type === 'buildup') {
      if (!session.buildupData) {
        return (
          <Card size="md" className="text-center text-lc-gray-600">
            {t('learn.runner.noBuildup')}
          </Card>
        )
      }
      return (
        <BuildupView
          componentProgressId={session.componentProgressId}
          enrollmentId={enrollmentId}
          componentType={component.type}
          label={pickComponentLabel(component, i18n.language, labelFallbacks)}
          buildupData={session.buildupData}
          isDesignatedReview={isDesignatedReview}
          runtimeMode={runtimeMode}
          onComplete={handleChildComplete}
        />
      )
    }

    if (type === 'warmup') {
      return (
        <WarmupView
          componentProgressId={session.componentProgressId}
          enrollmentId={enrollmentId}
          componentType={component.type}
          label={pickComponentLabel(component, i18n.language, labelFallbacks)}
          stepNumber={currentIndex + 1}
          warmupData={session.warmupData ?? null}
          runtimeMode={runtimeMode}
          learningAttemptId={runtimeMode === 'live' ? session.learningAttemptId ?? null : null}
          onComplete={handleChildComplete}
        />
      )
    }

    return (
      <GuideView
        componentProgressId={session.componentProgressId}
        enrollmentId={enrollmentId}
        componentType={component.type}
        label={pickComponentLabel(component, i18n.language, labelFallbacks)}
        guideData={session.guideData ?? null}
        runtimeMode={runtimeMode}
        onComplete={handleChildComplete}
      />
    )
  }

  const progressPercent = Math.min(
    100,
    Math.round((currentIndex / Math.max(1, totalCount)) * 100),
  )
  const showWrongReviewIntro =
    !loading &&
    !error &&
    runtimeMode === 'live' &&
    isDesignatedReview &&
    isWrongReview &&
    session?.quizData != null &&
    !wrongReviewIntroAcknowledged

  if (headless) {
    return (
      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        <main
          className={
            usesV3VideoLayout ||
            isToonComponent ||
            isQuizComponent ||
            isWarmupComponent ||
            isBuildupComponent
              ? 'flex-1 min-h-0 w-full min-w-0 overflow-y-auto p-4 md:p-10'
              : 'mx-auto w-full max-w-[1024px] min-w-0 flex-1 px-4 py-6 md:px-8 md:py-10'
          }
        >
          {loading && <LearnerLoading />}
          {!loading && error && (
            <Card
              size="md"
              role="alert"
              aria-live="assertive"
              className="flex flex-col items-center gap-4 py-8 text-center"
            >
              <div
                aria-label={t('aria.error')}
                className="w-10 h-10 rounded-full bg-lc-red-900 text-white flex items-center justify-center text-[24px] font-bold"
              >
                ✕
              </div>
              <p className="text-[18px] leading-[1.5] tracking-[-0.6px] font-medium text-lc-gray-700">
                {error}
              </p>
              <Button3D variant="lightblue" size="M" onClick={handleRetry}>
                {t('learn.runner.retry')}
              </Button3D>
            </Card>
          )}
          {!loading && !error && !showWrongReviewIntro && renderRunner()}
        </main>
        {showWrongReviewIntro && (
          <WrongReviewIntroDialog onConfirm={() => setWrongReviewIntroAcknowledged(true)} />
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-screen bg-lc-gray-c100">
      {/* Top App Bar — DESIGN.md §5/§4.2 패턴 */}
      <header className="sticky top-0 z-20 bg-white shadow-lc-soft">
        <div className="flex items-center gap-3 px-4 h-[72px] max-w-[1024px] mx-auto w-full">
          <button
            onClick={onBack}
            aria-label={t('aria.exit')}
            className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white border border-lc-gray-200 shadow-lc-chip text-lc-gray-700 hover:bg-lc-gray-100 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-lightblue-900"
          >
            <span className="text-xl leading-none">←</span>
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[14px] leading-[1.3] tracking-[-0.6px] font-light text-lc-gray-500 truncate">
              {component.type.toUpperCase()}
            </p>
            <h2 className="text-[18px] leading-[1.5] tracking-[-0.6px] font-medium text-lc-gray-800 truncate">
              {pickComponentLabel(component, i18n.language, labelFallbacks)}
            </h2>
          </div>
          <span className="shrink-0 text-[18px] leading-[1.5] tracking-[-0.6px] font-medium text-lc-lightblue-900 tabular-nums">
            #{currentIndex + 1} / {totalCount}
          </span>
        </div>
        {/* Progress strip */}
        <div className="max-w-[1024px] mx-auto w-full px-4 pb-2">
          <div className="h-1.5 w-full bg-lc-gray-200 rounded-lc-pill overflow-hidden">
            <div
              className="h-full bg-lc-lightblue-900 rounded-lc-pill transition-all duration-300 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="flex-1 px-4 py-6 md:px-8 md:py-10 max-w-[1024px] mx-auto w-full">
        {loading && <LearnerLoading />}

        {!loading && error && (
          <Card
            size="md"
            role="alert"
            aria-live="assertive"
            className="flex flex-col items-center gap-4 py-8 text-center"
          >
            <div
              aria-label={t('aria.error')}
              className="w-10 h-10 rounded-full bg-lc-red-900 text-white flex items-center justify-center text-[24px] font-bold"
            >
              ✕
            </div>
            <p className="text-[18px] leading-[1.5] tracking-[-0.6px] font-medium text-lc-gray-700">
              {error}
            </p>
            <Button3D variant="lightblue" size="M" onClick={handleRetry}>
              {t('learn.runner.retry')}
            </Button3D>
          </Card>
        )}

        {!loading && !error && !showWrongReviewIntro && renderRunner()}
      </main>
      {showWrongReviewIntro && (
        <WrongReviewIntroDialog onConfirm={() => setWrongReviewIntroAcknowledged(true)} />
      )}
    </div>
  )
}

/**
 * Library-review 응답(LcLibraryStartResponse) → ComponentRunner 가 소비하는
 * LcStartSessionResponse 모양으로 변환. 퀴즈는 격리 세션을 생성하므로 sessionId /
 * componentProgressId 가 non-null(이어풀기·응답 기록에 사용), 비-퀴즈는 null.
 * isFirstAttempt 는 항상 false(복습).
 */
function adaptLibraryStartResponse(
  r: LcLibraryStartResponse,
): LcStartSessionResponse {
  return {
    sessionId: r.sessionId,
    componentProgressId: r.componentProgressId,
    isFirstAttempt: false,
    videoData: r.videoData,
    quizData: r.quizData,
    guideData: r.guideData as LcStartSessionResponse['guideData'],
    toonData: r.toonData as LcStartSessionResponse['toonData'],
    buildupData: r.buildupData,
    toolData: r.toolData as LcStartSessionResponse['toolData'],
    warmupData: r.warmupData as LcStartSessionResponse['warmupData'],
  }
}
